/* 216 addendum 5 — `import React` for `react/react-in-jsx-scope`, the rule's own
   remedy (166's reading, AgendaView.tsx): the ratchet counts the rule once per
   JSX element, §9 mounts one more here, and putting React in scope clears the
   same debt for every element already in the file. Nothing at runtime changes. */
import React from "react";
import { useState, useRef, useEffect } from "react";
import type { Dispatch } from "react";
import { loadXlsx, type Xlsx } from "../lib/loadOnUse.mjs"; // 208 §1 — the Excel library loads the first time a sheet is read or written
import type { Translate } from "../lib/interestRefusal"; // 209 §7 — the translator, typed where it is called
import { xlsxRow } from "../lib/xlsxCells.mjs";
import {
  Pencil,
  Trash2,
  Plus,
  Upload,
  Download,
  Search,
  BookOpen,
  FileText,
  Check,
  BookMarked,
  Camera,
  RefreshCw,
  Paperclip,
  ExternalLink,
  FolderPlus,
} from "lucide-react";
import { I, SK, fd } from "../utils";
import { tempId } from "../lib/tempId"; // 207 §1.2
import {
  LockButton,
  InitialsBadge,
  AddButton,
  ModalLayout,
  SortCaret, // 200-glance §M — the one sort caret
} from "../components/SharedUI";
import { usePermissions } from "../hooks/usePermissions";
import { useTranslation } from "../i18n";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { resolveTaxonomyLabel } from "../lib/taxonomyHelpers";
import { pickEntryLabel, isDividerEntry } from "../utils/taxonomyMap";
import {
  api,
  borrowBook as apiBorrow,
  returnBook as apiReturn,
  planBookPdf,
  uploadBookPdf,
  detachBookPdf,
  copyBookToCase,
  copyBookToCollectiveCase,
  createBook as apiCreateBook,
  getBooks as apiGetBooks,
  importBooks as apiImportBooks,
} from "../api";
import type { LibraryImportPlan, LibraryImportRow } from "../api";
import LibraryImportModal, { type LibraryImportMode } from "../components/LibraryImportModal"; // 216 addendum 5 §9
import type { ToastFn } from "../components/Toast"; // 216 addendum 5 — the host's toast, read through its own type once
import Combobox from "../components/Combobox";
import MenuSelect from "../components/MenuSelect"; // 154B §2
import { CT, RAD, FS, Z } from "../lib/tokens";
import { useConfirm } from "../components/useConfirmAndUndo";
import Table from "../components/Table";
import Pager from "../components/Pager";
import { pageOf } from "../hooks/usePager";

const PG_SIZE = 20;

// Auto-fillable fields from an ISBN lookup. Used by skip-touched policy:
// when the user runs Lookup, only fields NOT in mod._touched receive new
// values. ISBN itself is excluded — it is the lookup key, not a result.
const LOOKUP_FIELDS = ["title", "author", "publisher", "year"];

// Phase 16 Correction A — server-side proxy at /api/lookup-isbn cascades
// across Google Books, BnF (French legal corpus), KB.nl (Dutch legal
// corpus), and Open Library, picking the most-likely catalog per ISBN
// prefix. Belgian legal publishers (Larcier, Bruylant, Anthemis,
// Intersentia, die Keure) live in BnF / KB.nl, which the client cannot
// hit directly (no CORS on the SRU endpoints). Same {title, author,
// publisher, year, _source} contract as before so the modal autofill
// code does not change shape.
// 163 §B.3 — minimal shapes for awaited api() results read in this view.
type BookRow = { id?: number | string; [k: string]: unknown };
type PdfActionRes = {
  book?: BookRow;
  suffixApplied?: boolean;
  filename?: string;
} | null;
type IsbnLookup = { _source?: string; [k: string]: unknown };
async function lookupISBN(isbn: any): Promise<IsbnLookup | null> {
  const clean = String(isbn || "").replace(/[^0-9Xx]/g, "");
  if (!clean) return null;
  try {
    return (await api.get(
      `/lookup-isbn?isbn=${encodeURIComponent(clean)}`,
    )) as IsbnLookup | null;
  } catch {
    return null;
  }
}

// Source-name → human label, used in the post-lookup toast so users
// know which catalog filled their fields. Maps back from the proxy's
// _source token.
// 59 §3 — the catalogue names are PROPER NOUNS and read the same in the three
// languages, but they still reach a screen, so they come from the dictionary
// like everything else. Keyed, seeded identically in en/fr/nl; the fallback is
// what an unseeded box renders.
const SOURCE_LABELS: Record<string, [string, string]> = {
  google: ["library.source.google", "Google Books"],
  bnf: ["library.source.bnf", "BnF"],
  kb: ["library.source.kb", "KB.nl"],
  openlibrary: ["library.source.openlibrary", "Open Library"],
};

// Boolean parser for xlsx cells. Accepts the common Excel exports of
// boolean-ish values across EN / FR / NL spreadsheets that legal staff
// hand-edit. The `emptyDefault` arg (Phase 16 #3 F) lets callers route
// empty / NaN cells to a per-column default — PAPER defaults to true
// (most legal books are physical), PDF and STRADA default to false.
// Explicit YES/NO/OUI/NON/TRUE/FALSE/0/1/X still wins over the default.
function parseBool(v: any, emptyDefault = false) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (v == null) return emptyDefault === true;
  const s = String(v).trim().toLowerCase();
  if (s === "") return emptyDefault === true;
  if (
    s === "true" ||
    s === "1" ||
    s === "yes" ||
    s === "oui" ||
    s === "ja" ||
    s === "x"
  )
    return true;
  if (s === "false" || s === "0" || s === "no" || s === "non" || s === "nee")
    return false;
  return emptyDefault === true;
}

// Phase 16 correction #5 (N) — display-only truncation to N words. Used
// for Publ./Journ. and Section in the Library table so long values
// ("Kluwer a Wolters Kluwer business", "Bankruptcy law and law of
// companies in difficulty") don't eat Title's width. Full value remains
// available via the cell's title attribute on hover.
function twoWords(v: any) {
  const s = String(v ?? "").trim();
  if (!s) return "";
  const parts = s.split(/\s+/);
  if (parts.length <= 2) return s;
  return parts.slice(0, 2).join(" ") + "…";
}

const LibraryView = ({
  books,
  setBooks,
  co,
  currentUser,
  toast,
  pname,
  sv,
  s,
}: any) => {
  const { t } = useTranslation();
  const askConfirm = useConfirm();
  const { entries: secEntries } = useTaxonomy("library_section");
  const [sort, setSort] = useState({ k: "author", d: 1 });
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [acOpen, setAcOpen] = useState(false);
  const [secFilter, setSecFilter] = useState("");
  const [locked, setLocked] = useState(true);
  const [mod, setMod] = useState<any>(null);
  const [delConfirm, setDelConfirm] = useState<any>(null);
  const [highlightId, setHighlightId] = useState<any>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [importReport, setImportReport] = useState<any>(null);
  /* 216 addendum 5 §9 [RULED 80] — the import's preview: the file's rows, the
     server's plan, the parse warnings, the mode chosen, and whether the write
     is under way. Null = no window. */
  const [importAsk, setImportAsk] = useState<{
    rows: LibraryImportRow[];
    plan: LibraryImportPlan;
    warnings: { row: number; msg: string }[];
    mode: LibraryImportMode;
    busy: boolean;
  } | null>(null);
  // Phase 18 — modal-scoped state for 000BIB attachment. pdfPlan caches
  // the most recent /pdf-plan response (deterministic filename + exists
  // boolean); pdfStaged holds the in-modal pending File before upload
  // commits. Both reset when the modal closes.
  const [pdfPlan, setPdfPlan] = useState<any>(null);
  const [pdfStaged, setPdfStaged] = useState<any>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  // Phase 18 — case-picker overlay opened from the Add/Edit Book modal
  // when an attached PDF is present. Lazy-loads /cases + /collective-cases
  // on first open (only the user's visible scope; api enforces).
  const [caseCopy, setCaseCopy] = useState<any>(null); // { bookId, busy } | null
  const [caseList, setCaseList] = useState<any>(null);
  const [ccList, setCcList] = useState<any>(null);
  const [caseCopyQ, setCaseCopyQ] = useState("");
  const fileRef = useRef<any>(null);
  const pdfFileRef = useRef<any>(null);
  const acRef = useRef<any>(null);
  const tableRef = useRef<any>(null);

  const _perms = usePermissions();
  // Phase 16 correction #4 (M) — final library permission model:
  //   lateral.library       → view + borrow + return + ADD (Wikipedia-
  //                           style contribution; anyone who can see
  //                           the catalog can add a new entry).
  //   lateral.library.edit  → edit existing, delete, lock toggle,
  //                           PAPER/PDF/STRADA quick-toggles, Excel
  //                           import (catalog curation).
  const _canView = _perms.can("lateral.library.view");
  const _canEditCatalog = _perms.can("lateral.library.edit");
  const canAdd = _canView;
  const canEdit = _canEditCatalog;
  const canFlags = _canEditCatalog;
  const canManage = _canEditCatalog;
  // Force-locked when the user lacks the edit gate. The state variable
  // still exists (init true) so the rest of the file's gating logic keeps
  // working; users without the gate simply can't unlock, and we hide the
  // toggle entirely.
  const lockEffective = locked || !_canEditCatalog;

  useEffect(() => {
    if (!acOpen) return;
    const h = (e: any) => {
      if (acRef.current && !acRef.current.contains(e.target)) setAcOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [acOpen]);

  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(null), 2200);
    return () => clearTimeout(t);
  }, [highlightId]);

  // Phase 18 — reset 000BIB attachment modal state when the modal closes
  // or when the user pivots from one book to another. Avoids leaking a
  // previously-staged File or stale dedup plan into the next session.
  useEffect(() => {
    if (!mod || !mod.id) {
      setPdfPlan(null);
      setPdfStaged(null);
    }
  }, [mod?.id]);

  const persist = (nb: any) => s(SK.books, nb, setBooks);

  // Phase 14 — Section is now a closed-list taxonomy code. Books carry the
  // code (e.g. "PEN"); rendering goes through resolveTaxonomyLabel for the
  // active-language label. Sort and filter keys also operate on the
  // resolved label so the UX stays language-aware.
  const labelFor = (code: any) => resolveTaxonomyLabel(code, secEntries);

  const filtered = books.filter((b: any) => !secFilter || b.section === secFilter);
  const sorted = [...filtered].sort((a, b) => {
    const k = sort.k;
    let av = a[k] ?? "",
      bv = b[k] ?? "";
    if (k === "year") {
      av = +av || 0;
      bv = +bv || 0;
    } else if (k === "paper" || k === "pdf" || k === "strada") {
      av = av ? 1 : 0;
      bv = bv ? 1 : 0;
    } else if (k === "section") {
      av = String(labelFor(av) || "").toLowerCase();
      bv = String(labelFor(bv) || "").toLowerCase();
    } else if (k === "borrowedBy") {
      // Phase 18 — canonical wire shape: borrowedById (FK integer) is the
      // identity source of truth; borrowedBy (embedded Collaborator object,
      // present on API responses) is for display only. Sort uses the
      // integer id to look up the display name so the column sorts
      // correctly regardless of whether the row came in via API fetch or
      // a legacy local write.
      const aid = a.borrowedById ?? null;
      const bid = b.borrowedById ?? null;
      av = aid
        ? co.find((c: any) => c.id === aid)
          ? pname(co.find((c: any) => c.id === aid))
          : ""
        : "";
      bv = bid
        ? co.find((c: any) => c.id === bid)
          ? pname(co.find((c: any) => c.id === bid))
          : ""
        : "";
    } else {
      av = String(av).toLowerCase();
      bv = String(bv).toLowerCase();
    }
    if (av < bv) return -1 * sort.d;
    if (av > bv) return 1 * sort.d;
    return 0;
  });

  /* 188 D.3 — the arithmetic is `pageOf`'s at THIS list's own page size, twenty
     (`Math.max(1, ceil)` · `Math.min(page, tot - 1)` · the slice — the copy's
     three expressions, identity replayed over 4,800 cases, 0 differences);
     `PG_SIZE` stays the name the ISBN-lookup jump reads (`targetPage`). The
     control's words are `common.*` where this copy read `pagination.*` —
     D.2 compared the rows in en, fr and nl: identical words, only the
     placeholder names differed (`{tot}` here, `{total}` there). */
  const { cur: curPage, tot: totPages, pageItems: pageRows } = pageOf(sorted, page, PG_SIZE);

  // Phase 16 correction #2 — checkbox-scoped accent colours. Style lives
  // ON THE CHECKBOX, never on the row, so an in-progress borrow doesn't
  // bleed across other visual signals. PAPER: muted green when on shelf,
  // muted red when out. PDF/STRADA: muted green when checked (not
  // physically borrowable). Unchecked boxes carry no override so the
  // browser falls back to theme default.
  //
  // isBorrowed accepts BOTH shapes — borrowedById (server FK column) and
  // borrowedBy (sometimes an int from legacy local writes, sometimes the
  // embedded Collaborator object from API). Either being truthy means
  // the book is out.
  const isBorrowed = (book: any) => book?.borrowedById != null || !!book?.borrowedBy;
  const paperAccent = (book: any) =>
    book?.paper ? (isBorrowed(book) ? "var(--rdm)" : "var(--gnm)") : undefined;
  const greenAccent = (checked: any) => (checked ? "var(--gnm)" : undefined);

  // Phase 18.2 — bumped slice cap from 8 -> 16 so the dropdown can
  // actually fill its new (roughly doubled) maxHeight. The CSS cap
  // below uses min(560px, 60vh) so the panel still clips inside the
  // viewport on short screens (small laptops, secondary monitors).
  const acMatches =
    q.trim().length >= 1
      ? books
          .filter((b: any) => {
            const ql = q.toLowerCase();
            return (
              (b.author || "").toLowerCase().includes(ql) ||
              (b.title || "").toLowerCase().includes(ql) ||
              (b.publisher || "").toLowerCase().includes(ql) ||
              (b.isbn || "").toLowerCase().includes(ql) ||
              (b.code || "").toLowerCase().includes(ql) ||
              String(labelFor(b.section) || "")
                .toLowerCase()
                .includes(ql)
            );
          })
          .slice(0, 16)
      : [];

  const thClick = (k: any) => setSort((p) => ({ k, d: p.k === k ? -p.d : 1 }));
  const thArrow = (k: any) => <SortCaret active={sort.k === k} d={sort.d} />; // 200-glance §M — the one sort caret

  const updField = (id: any, field: any, value: any) => {
    persist(books.map((b: any) => (b.id === id ? { ...b, [field]: value } : b)));
  };

  const onSelectAc = (b: any) => {
    setQ("");
    setAcOpen(false);
    const idx = sorted.findIndex((x) => x.id === b.id);
    if (idx >= 0) {
      const targetPage = Math.floor(idx / PG_SIZE);
      setPage(targetPage);
      setHighlightId(b.id);
      setTimeout(() => {
        const el = document.getElementById(`bk-${b.id}`);
        if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 50);
    }
  };

  // Field-edit tracking for the ISBN-lookup skip-touched policy. Any field
  // the user types into is marked "touched"; lookup never overwrites those.
  const touch = (k: any) =>
    setMod((p: any) => ({ ...p, _touched: { ...(p?._touched || {}), [k]: true } }));
  const setF = (k: any, v: any) =>
    setMod((p: any) => ({
      ...p,
      [k]: v,
      _touched: { ...(p?._touched || {}), [k]: true },
    }));

  // Phase 18 — Mac file picker for 000BIB attachments. Stages a File in
  // pdfStaged + asks the server for the deterministic filename and dedup
  // status (pdfPlan). The user then clicks Upload PDF to commit. Clearing
  // the staged file resets both pieces of state.
  const onPdfPicked = async (file: any) => {
    if (!file) return;
    setPdfStaged(file);
    // Phase 18.1 — in Add mode (no id yet) the upload happens at Save
    // time after the Book row is created. The 000BIB dedup plan needs
    // an id, so we skip the plan call here; saveBook still runs the
    // server-side dedup (the upload endpoint re-lists 000BIB and
    // applies a collision suffix). Skipping plan in Add means the
    // in-modal clash warning is Edit-only — an acceptable narrowing
    // since the alternative would require two trips to the server.
    if (!mod?.id) return;
    setPdfBusy(true);
    try {
      const plan = await planBookPdf(mod.id);
      setPdfPlan(plan);
    } catch (e: any) {
      const code = e?.code || "";
      const msg =
        code === "m365.not_connected"
          ? t("library.m365_not_connected", {
              fallback:
                "Microsoft 365 not connected — go to Settings to connect.",
            })
          : e?.message ||
            t("library.plan_failed", {
              fallback: "Could not check the bibliography folder.",
            });
      toast("⚠ " + msg);
    } finally {
      setPdfBusy(false);
    }
  };

  // Convert a File to base64 (without the data: prefix) for the JSON
  // upload payload. FileReader is the only browser-native path.
  const fileToBase64 = (file: any) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onerror = () => reject(r.error || new Error("read_failed"));
      r.onload = () => {
        const s = String(r.result || "");
        const idx = s.indexOf(",");
        resolve(idx >= 0 ? s.slice(idx + 1) : s);
      };
      r.readAsDataURL(file);
    });

  const onPdfUpload = async () => {
    if (!mod?.id || !pdfStaged) return;
    setPdfBusy(true);
    try {
      const b64 = await fileToBase64(pdfStaged);
      const ctype = pdfStaged.type || "application/pdf";
      const res = (await uploadBookPdf(mod.id, b64, ctype)) as PdfActionRes;
      // Merge the freshly-returned Book row into local state so the table
      // open-link goes live without a refresh.
      if (res && res.book) {
        setBooks((prev: any) =>
          (prev || []).map((b: any) => (b.id === mod.id ? res.book : b)),
        );
        setMod((p: any) => (p ? { ...p, ...res.book } : p));
      }
      setPdfStaged(null);
      setPdfPlan(null);
      const msg = res?.suffixApplied
        ? t("library.uploaded_with_suffix", {
            name: res.filename,
            fallback: `Uploaded as ${res.filename} (renamed to avoid a clash)`,
          })
        : t("library.uploaded", {
            name: res?.filename || "",
            fallback: "PDF uploaded",
          });
      toast(msg);
    } catch (e: any) {
      const code = e?.code || "";
      const msg =
        code === "m365.not_connected"
          ? t("library.m365_not_connected", {
              fallback:
                "Microsoft 365 not connected — go to Settings to connect.",
            })
          : e?.message ||
            t("library.upload_failed", { fallback: "Upload failed" });
      toast("⚠ " + msg);
    } finally {
      setPdfBusy(false);
    }
  };

  // Phase 18 — bib-side copy entry. Opens a small picker over the modal
  // and dispatches to the case or cc endpoint based on the picked row.
  const openCasePicker = (bookId: any) => {
    setCaseCopy({ bookId, busy: false });
    setCaseCopyQ("");
    if (caseList === null) {
      api
        .get("/cases")
        .then((rs) => setCaseList(Array.isArray(rs) ? rs : []))
        .catch(() => setCaseList([]));
    }
    if (ccList === null) {
      api
        .get("/collective-cases")
        .then((rs) => setCcList(Array.isArray(rs) ? rs : []))
        .catch(() => setCcList([]));
    }
  };
  const doCasePick = async (target: any) => {
    if (!caseCopy?.bookId) return;
    setCaseCopy((p: any) => ({ ...(p || {}), busy: true }));
    try {
      const fn =
        target.kind === "cc" ? copyBookToCollectiveCase : copyBookToCase;
      const r = (await fn(target.id, caseCopy.bookId)) as {
        status?: string;
      } | null;
      if (r?.status === "duplicate")
        toast(
          t("library.already_copied", {
            fallback: "Already in that case folder",
          }),
        );
      else
        toast(
          t("library.copied_to_case", {
            ref: target.ref,
            fallback: "Copied to " + target.ref,
          }),
        );
      setCaseCopy(null);
    } catch (e: any) {
      const code = e?.code || "";
      const msg =
        code === "libraryCopy.no_subfolder"
          ? t("library.no_subfolder_configured", {
              fallback: "Admin has not configured a library-copy subfolder.",
            })
          : code === "libraryCopy.no_case_folder"
            ? t("library.no_case_folder", {
                fallback: "That case has no linked SharePoint folder.",
              })
            : e?.message ||
              t("library.copy_failed", { fallback: "Copy failed" });
      toast("⚠ " + msg);
      setCaseCopy((p: any) => ({ ...(p || {}), busy: false }));
    }
  };

  const onPdfDetach = async () => {
    if (!mod?.id) return;
    askConfirm({
      // 161C §3 — the verb is REMOVE (a link), not delete (a file); the title
      // has to say which, because the body is what explains the difference.
      title: t("confirm.detach_pdf_title", { fallback: "Remove the PDF link?" }),
      message: t("library.detach_confirm", {
        fallback:
          "Remove the PDF link from this book? The file in 000BIB will not be deleted.",
      }),
      onConfirm: async () => {
        setPdfBusy(true);
        try {
          const res = (await detachBookPdf(mod.id)) as PdfActionRes;
          if (res && res.book) {
            setBooks((prev: any) =>
              (prev || []).map((b: any) => (b.id === mod.id ? res.book : b)),
            );
            setMod((p: any) => (p ? { ...p, ...res.book } : p));
          }
          toast(t("library.detached", { fallback: "PDF detached" }));
        } catch (e: any) {
          toast(
            "⚠ " +
              (e?.message ||
                t("library.detach_failed", { fallback: "Detach failed" })),
          );
        } finally {
          setPdfBusy(false);
        }
      },
    });
  };

  const saveBook = async () => {
    if (!mod.author || !mod.title) {
      toast(
        t("library.author_title_required", {
          fallback: "⚠ Author and Title are required",
        }),
      );
      return;
    }
    // Phase 16 correction #2 — borrowedBy / borrowedDate are NOT part of
    // the modal save payload. They are owned by the explicit Borrow /
    // Return actions which hit dedicated endpoints; this preserves
    // whatever the server has for those fields when editing other
    // metadata.
    const data = {
      author: mod.author || "",
      title: mod.title || "",
      publisher: mod.publisher || "",
      year: mod.year || "",
      section: mod.section || "",
      isbn: mod.isbn || "",
      code: mod.code || "",
      paper: !!mod.paper,
      pdf: !!mod.pdf,
      strada: !!mod.strada,
    };
    // Phase 18.1 — split by mode AND by staged-PDF presence.
    //
    // Edit + staged: still refuse silent saves while an upload is
    //   pending (the row already has an id; user can click Upload PDF
    //   then Save, or Clear the picker first). The optimistic s()
    //   path doesn't carry the file, so saving here would drop the
    //   attachment without warning.
    //
    // Edit + no staged: optimistic persist via s(), unchanged.
    //
    // Add + staged: a file can only attach once the Book row exists,
    //   so we do create → upload directly via the api.js client
    //   (bypassing the optimistic s() path which doesn't return the
    //   server-assigned id synchronously). The resulting row gets
    //   merged into local state.
    //
    // Add + no staged: optimistic persist via s(), unchanged.
    if (mod.id && pdfStaged) {
      toast(
        t("library.upload_pending", {
          fallback: "⚠ Upload the PDF first, or remove it from the picker.",
        }),
      );
      return;
    }
    if (!mod.id && pdfStaged) {
      setPdfBusy(true);
      try {
        const created = (await apiCreateBook(data)) as BookRow | null;
        if (!created || created.id == null)
          throw new Error("create returned no id");
        const b64 = await fileToBase64(pdfStaged);
        const ctype = pdfStaged.type || "application/pdf";
        let finalRow = created;
        try {
          const upRes = (await uploadBookPdf(
            created.id,
            b64,
            ctype,
          )) as PdfActionRes;
          if (upRes && upRes.book) finalRow = upRes.book;
          toast(
            upRes?.suffixApplied
              ? t("library.added_uploaded_with_suffix", {
                  name: upRes.filename,
                  fallback: `Book added. PDF uploaded as ${upRes.filename} (renamed to avoid a clash).`,
                })
              : t("library.added_uploaded", {
                  fallback: "Book added with PDF.",
                }),
          );
        } catch (e: any) {
          // The Book row IS created. The upload failed — surface that
          // distinctly so the user knows the row landed and can retry
          // the attach from the Edit modal.
          const code = e?.code || "";
          const msg =
            code === "m365.not_connected"
              ? t("library.m365_not_connected", {
                  fallback:
                    "Microsoft 365 not connected — go to Settings to connect.",
                })
              : e?.message ||
                t("library.upload_failed", { fallback: "Upload failed" });
          toast(
            "⚠ " +
              t("library.added_upload_failed", {
                fallback: "Book added but PDF upload failed",
              }) +
              ": " +
              msg,
          );
        }
        setBooks((prev: any) => {
          const arr = Array.isArray(prev) ? prev : [];
          const existing = arr.find((b) => b.id === finalRow.id);
          if (existing)
            return arr.map((b) => (b.id === finalRow.id ? finalRow : b));
          return [...arr, finalRow];
        });
        setMod(null);
        setPdfPlan(null);
        setPdfStaged(null);
      } catch (e: any) {
        toast(
          "⚠ " +
            (e?.message ||
              t("library.add_failed", { fallback: "Could not add the book" })),
        );
      } finally {
        setPdfBusy(false);
      }
      return;
    }
    if (mod.id) {
      persist(books.map((b: any) => (b.id === mod.id ? { ...b, ...data } : b)));
      toast(t("library.book_updated", { fallback: "Book updated" }));
    } else {
      persist([...books, { id: tempId(), ...data }]);
      toast(t("library.book_added", { fallback: "Book added" }));
    }
    setMod(null);
    setPdfPlan(null);
    setPdfStaged(null);
  };

  const delBook = (id: any) => {
    persist(books.filter((b: any) => b.id !== id));
    setDelConfirm(null);
    toast(t("common.deleted", { fallback: "Deleted" }));
  };

  // Phase 16 correction #2 — explicit Borrow / Return actions hit the
  // server's dedicated endpoints. Server pins borrowedById to req.user.id
  // on borrow (the client cannot pass it), and clears it on return. The
  // response carries the freshly-shaped book with the embedded
  // Collaborator object, which we merge into local state directly so we
  // don't trigger an extra PUT through the s() syncer.
  const onBorrow = async (id: any) => {
    try {
      const updated = await apiBorrow(id);
      setBooks((prev: any) => (prev || []).map((b: any) => (b.id === id ? updated : b)));
      toast(t("library.borrowed", { fallback: "Borrowed" }));
    } catch (e: any) {
      toast(
        "⚠ " +
          (e.message ||
            t("library.borrow_failed", { fallback: "Borrow failed" })),
      );
    }
  };

  const onReturn = async (id: any) => {
    try {
      const updated = await apiReturn(id);
      setBooks((prev: any) => (prev || []).map((b: any) => (b.id === id ? updated : b)));
      toast(t("library.returned", { fallback: "Returned" }));
    } catch (e: any) {
      toast(
        "⚠ " +
          (e.message ||
            t("library.return_failed", { fallback: "Return failed" })),
      );
    }
  };

  // Phase 16 correction #4 (L) — click anywhere on a non-action cell to
  // copy a formatted reference to the clipboard. Format:
  // "AUTHOR, TITLE, PUBLISHER, YEAR" with empty fields skipped so we
  // never emit "  , Title, , 2020". Uses navigator.clipboard.writeText;
  // most modern browsers grant write access without prompt on a
  // user-initiated click. On failure (insecure context / blocked
  // permission), the toast falls back to an error.
  const copyReference = async (b: any) => {
    const parts = [b.author, b.title, b.publisher, b.year]
      .map((v) => String(v ?? "").trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const text = parts.join(", ");
    try {
      await navigator.clipboard.writeText(text);
      toast(t("library.reference_copied", { fallback: "Reference copied" }));
    } catch (e: any) {
      toast(
        "⚠ " +
          (e.message || t("library.copy_failed", { fallback: "Copy failed" })),
      );
    }
  };

  const runLookup = async () => {
    const isbn = (mod?.isbn || "").trim();
    if (!isbn) {
      toast(
        t("library.lookup_no_isbn", { fallback: "⚠ Enter an ISBN first" }),
      );
      return;
    }
    setLookupBusy(true);
    try {
      const r = await lookupISBN(isbn);
      if (!r) {
        toast(
          t("library.lookup_not_found", {
            fallback: "⚠ No match found for this ISBN",
          }),
        );
        return;
      }
      // Skip-touched: only fill fields the user has not edited.
      const touched = mod._touched || {};
      const next = { ...mod };
      let filled = 0;
      for (const k of LOOKUP_FIELDS) {
        if (touched[k]) continue;
        if (r[k] && !next[k]) {
          next[k] = r[k];
          filled++;
        }
      }
      setMod(next);
      if (filled > 0) {
        const srcPair = SOURCE_LABELS[r._source as keyof typeof SOURCE_LABELS];
        const src = srcPair
          ? t(srcPair[0], { fallback: srcPair[1] })
          : r._source || t("library.source.unknown", { fallback: "lookup" });
        toast(
          t("library.lookup_filled", {
            count: filled,
            source: src,
            fallback: `Filled ${filled} field${filled !== 1 ? "s" : ""} from ${src}`,
          }),
        );
      } else {
        toast(
          t("library.lookup_nothing_to_fill", {
            fallback: "Match found, but all fields were already filled",
          }),
        );
      }
    } finally {
      setLookupBusy(false);
    }
  };

  // Auto-trigger lookup when scanner returns a code so the user gets the
  // full populated row in one action.
  const onScanned = async (code: any) => {
    setScanOpen(false);
    setMod((p: any) => ({ ...p, isbn: code }));
    // Allow the modal to re-render with the ISBN visible before kicking
    // off the network round-trip.
    setTimeout(async () => {
      setLookupBusy(true);
      try {
        const r = await lookupISBN(code);
        if (!r) {
          toast(
            t("library.lookup_not_found", {
              fallback: "⚠ No match found for this ISBN",
            }),
          );
          return;
        }
        setMod((p: any) => {
          const touched = p?._touched || {};
          const next = { ...p, isbn: code };
          let filled = 0;
          for (const k of LOOKUP_FIELDS) {
            if (touched[k]) continue;
            if (r[k] && !next[k]) {
              next[k] = r[k];
              filled++;
            }
          }
          if (filled > 0)
            toast(
              t("library.scan_filled", {
                count: filled,
                fallback: `Scanned + filled ${filled} field${filled !== 1 ? "s" : ""}`,
              }),
            );
          return next;
        });
      } finally {
        setLookupBusy(false);
      }
    }, 30);
  };

  // Section legend sheet — populated from the taxonomy at download time
  // so the workbook is self-documenting. Admins can hand the file to a
  // colleague who has never seen LexCRM and the legend tells them how
  // to map labels back to codes.
  const buildWorkbook = (XLSX: Xlsx) => {
    const wb = XLSX.utils.book_new();
    // Sheet 1 — Books. Header row in caps so it round-trips through
    // copy-paste into other systems (Excel preserves header casing).
    const booksRows = books.map((b: any) => ({
      TITLE: b.title || "",
      AUTHOR: b.author || "",
      PUBLISHER: b.publisher || "",
      YEAR: b.year || "",
      ISBN: b.isbn || "",
      SECTION: b.section || "",
      CODE: b.code || "",
      PAPER: !!b.paper,
      PDF: !!b.pdf,
      STRADA: !!b.strada,
      NOTE: b.notes || "",
      "BORROWED DATE": b.borrowedDate || "",
      BY:
        b.borrowedById != null
          ? co.find((c: any) => c.id === b.borrowedById)
            ? pname(co.find((c: any) => c.id === b.borrowedById))
            : ""
          : "",
    }));
    // LEGAL-LANE (round 2, Excel sweep) — NOTE is free text; cap-guarded.
    const ws1 = XLSX.utils.json_to_sheet(booksRows.map(xlsxRow), {
      header: [
        "TITLE",
        "AUTHOR",
        "PUBLISHER",
        "YEAR",
        "ISBN",
        "SECTION",
        "CODE",
        "PAPER",
        "PDF",
        "STRADA",
        "NOTE",
        "BORROWED DATE",
        "BY",
      ],
    });
    XLSX.utils.book_append_sheet(wb, ws1, "Books");
    // Sheet 2 — Section Codes legend.
    const legendRows = (secEntries || []).map((e: any) => ({
      code: e.code,
      FR: e.labelFr || "",
      NL: e.labelNl || "",
      EN: e.labelEn || e.label || "",
    }));
    const ws2 = XLSX.utils.json_to_sheet(legendRows.map(xlsxRow), {
      header: ["code", "FR", "NL", "EN"],
    });
    XLSX.utils.book_append_sheet(wb, ws2, "Section Codes");
    return wb;
  };

  const onDownload = () => {
    void (async () => {
    try {
      const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
      const wb = buildWorkbook(XLSX);
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `LexCRM Library ${stamp}.xlsx`); // 181 (ruling 1)
    } catch (err: any) {
      toast(
        t("library.download_failed", { fallback: "⚠ Download failed" }) +
          ": " +
          err.message,
      );
    }
    })();
  };

  // Header name normaliser. Accepts case + whitespace variants so a hand-
  // edited workbook does not silently lose columns to "Title " vs "TITLE".
  const norm = (s: any) =>
    String(s || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const parseWorkbook = (wb: any, XLSX: Xlsx) => {
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
    const warnings: any[] = [];
    const out: any[] = [];
    // Pre-index taxonomy by code AND by labels for forgiveness.
    const byCode = new Map(
      (secEntries || []).map((e: any) => [String(e.code).toUpperCase(), e.code]),
    );
    const byLabel = new Map();
    for (const e of secEntries || []) {
      for (const f of ["labelEn", "labelFr", "labelNl", "label"]) {
        if (e[f]) byLabel.set(String(e[f]).trim().toLowerCase(), e.code);
      }
    }
    rows.forEach((rec: any, i: any) => {
      // Build lowercase-keyed access view so column-header case + spacing
      // does not matter.
      const r: Record<string, any> = {};
      for (const k of Object.keys(rec)) r[norm(k)] = rec[k];
      const title = String(r["title"] || "").trim();
      if (!title) return; // skip empty rows silently
      const rowNum = i + 2; // header is row 1 in user-facing terms
      // Section: accept code (preferred), or exact label match as fallback.
      const sRaw = String(r["section"] || "").trim();
      let section = null;
      if (sRaw && sRaw !== "-") {
        const upper = sRaw.toUpperCase();
        if (byCode.has(upper)) section = byCode.get(upper);
        else if (byLabel.has(sRaw.toLowerCase()))
          section = byLabel.get(sRaw.toLowerCase());
        else {
          warnings.push({
            row: rowNum,
            msg: `Unknown section "${sRaw}" — imported with no section.`,
          });
        }
      } else if (sRaw === "-") {
        warnings.push({
          row: rowNum,
          msg: 'SECTION was "-" — imported with no section.',
        });
      }
      // CODE: "A COMPLETER" → null + warn.
      let code = String(r["code"] || "").trim();
      if (code.toUpperCase() === "A COMPLETER") {
        warnings.push({
          row: rowNum,
          msg: 'CODE was "A COMPLETER" — imported as empty.',
        });
        code = "";
      }
      // Phase 18 D4 — xlsx import is CATALOG-ONLY. Borrow state (BY +
      // BORROWED DATE) is no longer accepted on import. If the
      // workbook still contains those columns we warn rather than
      // silently dropping them, so importers know they need to use the
      // Borrow / Return actions in the UI.
      if (
        String(r["by"] || "").trim() ||
        String(r["borrowed date"] || "").trim()
      ) {
        warnings.push({
          row: rowNum,
          msg: "BY / BORROWED DATE columns are ignored on import — use the Borrow / Return actions in the UI.",
        });
      }
      out.push({
        rowNum, // 216 addendum 5 §9 — the sheet's row, echoed on an ambiguous row
        title,
        author: String(r["author"] || "").trim(),
        publisher: String(r["publisher"] || "").trim(),
        year: String(r["year"] || "").trim(),
        isbn: String(r["isbn"] || "").trim(),
        section,
        code,
        // Phase 16 correction #3 (F): empty PAPER → true (Belgian legal
        // books are physical by default); PDF / STRADA stay false on
        // empty.
        paper: parseBool(r["paper"], true),
        pdf: parseBool(r["pdf"], false),
        strada: parseBool(r["strada"], false),
        notes: String(r["note"] || r["notes"] || "").trim(),
      });
    });
    return { books: out, warnings };
  };

  const onImport = (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
        const wb = XLSX.read((ev.target as any).result, { type: "array" });
        const { books: newBooks, warnings } = parseWorkbook(wb, XLSX);
        if (!newBooks.length) {
          toast(t("library.no_rows_found", { fallback: "⚠ No rows found" }));
          return;
        }
        /* 216 addendum 5 §9 [RULED 80] — THE IMPORT MATCHES BEFORE IT ADDS. Until
           this brief every row was appended as a new book (`persist([...books,
           ...withIds])`, one POST each): Laurent's 1 357 books, then all 2 092
           rows of his updated file. The server plans the import now (one route,
           one transaction — it owns what is persisted [ADVISOR]); the window
           shows the plan before anything is written. */
        const rows = newBooks as LibraryImportRow[];
        const plan = await apiImportBooks(rows, "preview");
        setImportAsk({ rows, plan, warnings: warnings as { row: number; msg: string }[], mode: "apply", busy: false });
      } catch (err: any) {
        toast(
          t("library.import_failed", { fallback: "⚠ Import failed" }) +
            ": " +
            err.message,
        );
      }
      e.target.value = "";
    };
    reader.readAsArrayBuffer(file);
  };
  /* 216 addendum 5 §9 — the write, in the mode chosen: the server answers what it
     wrote, the list is re-read whole (the window's rows are the server's, not
     the sync engine's diff), the words say the counts, the parse warnings keep
     their report. */
  const runImport = () => {
    const ask = importAsk;
    if (!ask || ask.busy) return;
    setImportAsk({ ...ask, busy: true });
    // the host's toast, the translator and the list's setter, read once through their types (the ratchet)
    const say = toast as ToastFn;
    const tr = t as Translate;
    const putBooks = setBooks as Dispatch<unknown[]>;
    void (async () => {
      try {
        const r = await apiImportBooks(ask.rows, ask.mode);
        const fresh = (await apiGetBooks()) as unknown[];
        putBooks(fresh);
        setImportAsk(null);
        const a = r.applied ?? { added: 0, updated: 0, removed: 0, unchanged: 0, ambiguous: 0, kept: 0 };
        say(
          tr("library.import.done", {
            added: a.added,
            updated: a.updated,
            removed: a.removed,
            kept: a.kept,
            fallback: `Library imported: ${String(a.added)} added, ${String(a.updated)} updated, ${String(a.removed)} removed, ${String(a.kept)} kept`,
          }),
        );
        setImportReport({ imported: a.added + a.updated, warnings: ask.warnings });
      } catch (err: unknown) {
        setImportAsk((p) => (p ? { ...p, busy: false } : p));
        const msg = err instanceof Error ? err.message : String(err);
        say(`⚠ ${tr("library.import_failed", { fallback: "⚠ Import failed" })}: ${msg}`, "error");
      }
    })();
  };

  // Phase 16 correction #2 — the day-based row tint (green→amber→red on
  // borrowedDate age) was removed: it duplicated the new checkbox-scoped
  // signal and made the whole row look "green" the moment a book got
  // borrowed, which is exactly the opposite of what a borrowed signal
  // should look like. PAPER accent colour carries the borrow-state cue
  // now; overdue alerts continue to flow through the App.jsx notification
  // panel.

  const coBadge = (c: any, size = 18) => {
    if (!c) return null;
    return <InitialsBadge {...({} as any)} person={c} isMe={c.id === currentUser} size={size} />;
  };

  // Sorted section options by resolved active-language label. Filter
  // dropdown uses the code as value; admins see the label.
  /* 110 §3a — the `|| e.code` stays for ordinary entries and must not apply
     to a divider (pickEntryLabel now returns "" for a headingless one, and
     re-falling-back here would reprint the `__div__` code this § removes). */
  const secOptions = (secEntries || [])
    .filter((e: any) => !e.archived)
    .map((e: any) => ({
      code: e.code,
      label: isDividerEntry(e) ? pickEntryLabel(e) : pickEntryLabel(e) || e.code,
    }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label));

  // BarcodeDetector is the Web standard. Available on Chrome / Edge /
  // Android stock browser. Safari (incl. iOS) currently lacks it — the
  // Scan button shows but is disabled, with a tooltip explaining why.
  const scanSupported =
    typeof window !== "undefined" && "BarcodeDetector" in window;

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <div
          ref={acRef}
          style={{
            position: "relative",
            flex: "1 1 280px",
            maxWidth: 420,
            minWidth: 200,
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              size={12}
              style={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--tm)",
                pointerEvents: "none",
              }}
            />
            <input
              className="field-input"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setAcOpen(true);
              }}
              onFocus={() => setAcOpen(true)}
              placeholder={t("library.search_placeholder", {
                fallback:
                  "Search by author, title, publisher, ISBN, code, section…",
              })}
              style={{
                margin: 0,
                padding: "5px 8px 5px 24px",
                fontSize: FS.s11,
                width: "100%",
                boxSizing: "border-box",
              }}
            />
          </div>
          {acOpen && acMatches.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                marginTop: 4,
                background: "var(--bc)",
                border: "1px solid var(--br)",
                borderRadius: RAD.r8,
                boxShadow: "0 6px 20px rgba(0,0,0,.6)",
                zIndex: Z.inlineMenu,
                maxHeight: "min(560px, 60vh)",
                overflowY: "auto",
              }}
            >
              {acMatches.map((b: any) => (
                <div
                  key={b.id}
                  onClick={() => onSelectAc(b)}
                  style={{
                    padding: "6px 10px",
                    cursor: "pointer",
                    fontSize: FS.s11,
                    borderBottom: "1px solid rgba(255,255,255,.04)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "var(--bh)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "none")
                  }
                >
                  <div style={{ color: "var(--tp)", fontWeight: 500 }}>
                    {b.title}
                  </div>
                  <div style={{ color: "var(--tm)", fontSize: FS.s9_5 }}>
                    {b.author}
                    {b.publisher ? " — " + b.publisher : ""}
                    {b.year ? " (" + b.year + ")" : ""}
                    {b.section
                      ? " · " + (labelFor(b.section) || b.section)
                      : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <MenuSelect
          className="field-input"
          value={secFilter}
          onChange={(v) => {
            setSecFilter(v);
            setPage(0);
          }}
          style={{
            margin: 0,
            padding: "5px 8px",
            fontSize: FS.s11,
            width: "auto",
            minWidth: 140,
          }}
          options={[
            ["", t("library.all_sections", { fallback: "All sections" })] as const,
            /* 110 §3a's divider is 108 §6b's row now — the menu draws it */
            ...secOptions.map(
              (o: any) => [String(o.code), String(o.label ?? "")] as const,
            ),
          ]}
        />

        {canManage && !lockEffective && (
          <button
            className="btn btn-ghost"
            style={{ padding: "4px 10px", fontSize: FS.s10 }}
            onClick={() => fileRef.current?.click()}
          >
            <Upload
              size={I.s}
              style={{ marginRight: 3, verticalAlign: "middle" }}
            />
            {t("library.import_excel", { fallback: "Import Excel" })}
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={onImport}
        />

        {!locked && (
          <button
            className="btn btn-ghost"
            style={{ padding: "4px 10px", fontSize: FS.s10 }}
            onClick={onDownload}
          >
            <Download
              size={I.s}
              style={{ marginRight: 3, verticalAlign: "middle" }}
            />
            {t("library.download_excel", { fallback: "Download Excel" })}
          </button>
        )}

        {/* 188 §F.1 [glance correction, Laurent 2026-09-14] — the Add button sits
            immediately left of the lock, after the two Excel buttons, the way
            SectionHeader's right group holds "+ Ajouter" and the lock on every
            section: one `marginLeft: auto` group at the far right (the same
            markup TeamAccessPanel's toolbar uses), so the pair stays together
            whether or not Add is showing. Until 188 the Add stood right after the
            section filter, alone on the left of the toolbar.
            Phase auth-C3 — decluttering: Add (book) and Download Excel join
            Import Excel behind the lock so the default toolbar stays light. Add
            stays open to any lateral.library user (no edit-gate) and uses bare
            `locked` so canAdd-only users can still reveal it by unlocking.
            Import Excel keeps its `canManage && !lockEffective` combo
            (edit-gated, plus the lock). Download Excel has no permission gate;
            it just hides until unlocked. LockButton renders for any library user
            (was canManage-only) because Add + Download Excel are behind the lock
            and need to be reachable by canAdd-only users too. lockEffective
            still gates edit/manage actions independently, so row edit/delete
            and Import Excel stay edit-gated. */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>
          {canAdd && !locked && (
            <AddButton {...({} as any)}
              label={t("common.add", { fallback: "Add" })}
              onClick={() => setMod({ paper: true, pdf: false, strada: false })}
            />
          )}
          <LockButton {...({} as any)}
            locked={locked}
            onToggle={() => setLocked((v) => !v)}
          />
        </div>
      </div>

      <div className="table-wrap" ref={tableRef}>
        <Table>
          <thead>
            <tr>
              {/* Phase 16 correction #4 (J) — Author column tightened to ~140px
                (was shrink-to-fit so long author lists expanded freely
                and squeezed Title). Title fills the remaining space and
                truncates at ~50 chars instead of 20, so most legal-book
                titles read fully. */}
              <th
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  width: 140,
                  maxWidth: 140,
                }}
                onClick={() => thClick("author")}
              >
                {t("library.author", { fallback: "Author" })}
                {thArrow("author")}
              </th>
              <th
                style={{ cursor: "pointer", userSelect: "none", width: "100%" }}
                onClick={() => thClick("title")}
              >
                {t("common.title", { fallback: "Title" })}
                {thArrow("title")}
              </th>
              <th
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  width: 1,
                }}
                onClick={() => thClick("publisher")}
              >
                {t("library.publisher", { fallback: "Publ./Journ." })}
                {thArrow("publisher")}
              </th>
              <th
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  width: 1,
                }}
                onClick={() => thClick("year")}
              >
                {t("library.year", { fallback: "Year" })}
                {thArrow("year")}
              </th>
              <th
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  width: 1,
                }}
                onClick={() => thClick("section")}
              >
                {t("library.section", { fallback: "Section" })}
                {thArrow("section")}
              </th>
              <th
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  width: 1,
                }}
                onClick={() => thClick("code")}
              >
                {t("library.code", { fallback: "Code" })}
                {thArrow("code")}
              </th>
              <th
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  width: 1,
                }}
                onClick={() => thClick("paper")}
              >
                {t("library.paper", { fallback: "Paper" })}
                {thArrow("paper")}
              </th>
              <th
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  width: 1,
                }}
                onClick={() => thClick("pdf")}
              >
                {t("library.pdf", { fallback: "PDF" })}
                {thArrow("pdf")}
              </th>
              <th
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  width: 1,
                }}
                onClick={() => thClick("strada")}
              >
                {t("library.strada", { fallback: "Strada" })}
                {thArrow("strada")}
              </th>
              <th
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  width: 1,
                  padding: "7px 4px",
                }}
                onClick={() => thClick("borrowedDate")}
              >
                {t("library.borrowed_date", { fallback: "Borrowed Date" })}
                {thArrow("borrowedDate")}
              </th>
              <th
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  width: 1,
                  padding: "7px 4px",
                  textAlign: "center",
                }}
                onClick={() => thClick("borrowedBy")}
              >
                {t("library.by", { fallback: "By" })}
                {thArrow("borrowedBy")}
              </th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr>
                <td
                  colSpan={12}
                  style={{
                    color: "var(--tm)",
                    fontSize: FS.s11,
                    textAlign: "center",
                    padding: "16px",
                  }}
                >
                  {q || secFilter
                    ? t("library.no_books_match", {
                        fallback: "No books match the current filter.",
                      })
                    : t("library.no_books_yet", { fallback: "No books yet." })}
                </td>
              </tr>
            )}
            {pageRows.map((b) => {
              // borrowedBy may arrive as an embedded Collaborator object (API
              // shape) or as an int (legacy local-state shape). Normalise to
              // an object for badge rendering by falling back to `co.find`
              // when borrowedBy is just an id.
              const borrowedC =
                b.borrowedBy && typeof b.borrowedBy === "object"
                  ? b.borrowedBy
                  : b.borrowedById != null
                    ? co.find((c: any) => c.id === b.borrowedById)
                    : b.borrowedBy
                      ? co.find((c: any) => c.id === b.borrowedBy)
                      : null;
              const isHi = b.id === highlightId;
              const rowBg = isHi ? "rgba(251,191,36,.35)" : null;
              return (
                <tr
                  key={b.id}
                  id={`bk-${b.id}`}
                  className="ttRow"
                  onClick={() => copyReference(b)}
                  style={{
                    cursor: "pointer",
                    ...(rowBg
                      ? { background: rowBg, transition: "background .3s" }
                      : {}),
                  }}
                >
                  {/* Phase 16 correction #5 (N) — column priority. Author is
                  capped at 140px / 18 chars. Publ./Journ. and Section
                  truncate to TWO WORDS so the cells stop expanding to
                  full content width. Title takes the remaining row width
                  and uses the table-cell ellipsis trick: width:100% +
                  maxWidth:0 makes the browser compute the cell as
                  "remaining space" and lets overflow:hidden +
                  textOverflow:ellipsis truncate at the rendered edge —
                  responsively, not at a fixed char count. Full values
                  remain available via the title attribute on hover.
                  Phase 16 correction #5 (O) — Author cell gets
                  position:relative so the row's instant-hover tooltip
                  span anchors above this cell on row hover. */}
                  <td
                    style={{
                      width: 140,
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      position: "relative",
                    }}
                    title={b.author}
                  >
                    {/* BRIEF 102 §1a — the cut goes and the comment above keeps
                        its own promise. It argues for the ellipsis trick on the
                        TITLE cell — "responsively, not at a fixed char count" —
                        while this cell, with the same three properties and its
                        own 140px, still cut at eighteen. */}
                    {b.author || ""}
                    <span className="ttRowTip">
                      {t("library.click_to_copy", {
                        fallback: "Click to copy reference",
                      })}
                    </span>
                  </td>
                  <td
                    style={{
                      width: "100%",
                      maxWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={b.title}
                  >
                    <span style={{ fontWeight: 500 }}>{b.title || ""}</span>
                  </td>
                  <td
                    style={{ whiteSpace: "nowrap" }}
                    title={b.publisher || ""}
                  >
                    {b.publisher ? twoWords(b.publisher) : "—"}
                  </td>
                  <td className="mono" style={{ whiteSpace: "nowrap" }}>
                    {b.year || "—"}
                  </td>
                  <td
                    style={{ whiteSpace: "nowrap" }}
                    title={b.section ? labelFor(b.section) || b.section : ""}
                  >
                    {b.section ? (
                      <span className="tag">
                        {twoWords(labelFor(b.section) || b.section)}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="mono" style={{ whiteSpace: "nowrap" }}>
                    {b.code || "—"}
                  </td>
                  {/* Phase 16 correction #3 (E) — checkbox is NEVER `disabled`,
                  because Chrome/Edge ignore accent-color on disabled
                  inputs (the box renders system gray, defeating the
                  borrow-state signal in locked mode). Read-only is
                  enforced by gating the onChange handler; visual cue is
                  pointer-events:none + opacity. */}
                  {(() => {
                    const editable = canFlags && !lockEffective;
                    const lockedStyle: any = editable
                      ? {}
                      : { pointerEvents: "none", opacity: 0.85 };
                    return (
                      <>
                        <td
                          style={{ textAlign: "center", width: 1 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={!!b.paper}
                            onChange={(e) => {
                              if (editable)
                                updField(b.id, "paper", e.target.checked);
                            }}
                            style={{
                              accentColor: paperAccent(b),
                              ...lockedStyle,
                            }}
                          />
                        </td>
                        {/* Phase 18 (Stage 3 correction) — three-state PDF
                      indicator. Green is reserved STRICTLY for "we hold
                      a saved PDF file" — gated on pdfWebUrl being
                      non-null, NOT on pdfFileName alone. A bare pdf or
                      strada flag (with no uploaded file) means "a
                      digital version exists elsewhere" and routes to
                      orange. Earlier rev #1 leaked green to two
                      adjacent states: pdfFileName-set-but-no-webUrl
                      (degenerate upload), and strada-only books
                      (which fell through to the unset checkbox in
                      the old predicate but were misread as the green
                      strada cell next door). */}
                        {(() => {
                          const hasFileLink = !!b.pdfWebUrl;
                          const existsElsewhere =
                            !hasFileLink &&
                            (b.pdfFileName || b.pdf || b.strada);
                          let cell;
                          if (hasFileLink) {
                            // Phase 18.3 — pair the open-link with a "save to
                            // case" affordance. The action is a library-USE,
                            // not a catalog-EDIT, so it gates on lateral.library
                            // (every viewer here), NOT lateral.library.edit.
                            // Server still enforces caseView.documents.edit on
                            // the destination case, and the picker pre-filters
                            // to writable cases so the user doesn't pick a
                            // dead-end. The open-link stays one-click — the
                            // save affordance sits alongside it, not in its
                            // path.
                            cell = (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 5,
                                }}
                              >
                                <a
                                  href={b.pdfWebUrl}
                                  target="_blank"
                                  rel="noopener"
                                  title={b.pdfFileName || ""}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    color: "var(--gn)",
                                    textDecoration: "none",
                                  }}
                                >
                                  <ExternalLink size={14} />
                                </a>
                                {_canView && (
                                  <button
                                    type="button"
                                    title={t("library.save_to_case_tooltip", {
                                      fallback:
                                        "Save a copy into a case folder",
                                    })}
                                    onClick={() => openCasePicker(b.id)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                      margin: 0,
                                      cursor: "pointer",
                                      color: "var(--ac)",
                                      display: "inline-flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <FolderPlus size={13} />
                                  </button>
                                )}
                              </span>
                            );
                          } else if (existsElsewhere) {
                            const tip = b.pdfFileName
                              ? t("library.pdf_attached_no_link", {
                                  fallback:
                                    "File attached but the open-link could not be resolved.",
                                })
                              : t("library.pdf_elsewhere", {
                                  fallback:
                                    "A digital version exists elsewhere — no file uploaded here.",
                                });
                            cell = (
                              <span
                                title={tip}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  color: "var(--am)",
                                }}
                              >
                                <FileText size={14} />
                              </span>
                            );
                          } else {
                            cell = (
                              <input
                                type="checkbox"
                                checked={!!b.pdf}
                                onChange={(e) => {
                                  if (editable)
                                    updField(b.id, "pdf", e.target.checked);
                                }}
                                style={{
                                  accentColor: greenAccent(b.pdf),
                                  ...lockedStyle,
                                }}
                              />
                            );
                          }
                          return (
                            <td
                              style={{ textAlign: "center", width: 1 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {cell}
                            </td>
                          );
                        })()}
                        <td
                          style={{ textAlign: "center", width: 1 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={!!b.strada}
                            onChange={(e) => {
                              if (editable)
                                updField(b.id, "strada", e.target.checked);
                            }}
                            style={{
                              accentColor: greenAccent(b.strada),
                              ...lockedStyle,
                            }}
                          />
                        </td>
                      </>
                    );
                  })()}
                  {/* Phase 16 correction #3 (I) — borrowedDate display is gated
                  on the borrower being set, not on the date column itself.
                  Defensive against any code path (legacy pre-#2 writes,
                  partial restores, manual DB edits, xlsx imports with a
                  date but no BY) that leaves an orphan date on a row
                  whose borrowedById is null. The DB-level cleanup
                  (server/scripts/seed_migration_phase16_orphan_borrow_date.js)
                  nulls the existing orphans on staging / prod; this
                  display gate stops them from ever surfacing again. */}
                  <td
                    className="mono"
                    style={{
                      whiteSpace: "nowrap",
                      width: 1,
                      padding: "6px 4px",
                    }}
                  >
                    {isBorrowed(b) && b.borrowedDate ? fd(b.borrowedDate) : "—"}
                  </td>
                  <td
                    style={{
                      width: 1,
                      padding: "6px 4px",
                      textAlign: "center",
                    }}
                  >
                    {borrowedC ? (
                      coBadge(borrowedC)
                    ) : (
                      <span style={{ color: "var(--tm)" }}>—</span>
                    )}
                  </td>
                  {/* Phase 16 correction #4 (L) — action cell stops row-click
                  propagation so Borrow / Return / Edit / Delete don't
                  double-fire the copy-reference handler. */}
                  <td
                    style={{ whiteSpace: "nowrap", textAlign: "right" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Phase 16 correction #2 — Borrow button pins the borrower
                    server-side to req.user.id; Return is available to any
                    authenticated user (a returned book is a physical fact).
                    No more "borrow on behalf" picker. */}
                    {isBorrowed(b) ? (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ padding: "2px 7px", fontSize: FS.s9 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onReturn(b.id);
                        }}
                      >
                        {t("library.return", { fallback: "Return" })}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ padding: "2px 7px", fontSize: FS.s9 }}
                        title={t("library.borrow_this_book", {
                          fallback: "Borrow this book",
                        })}
                        onClick={(e) => {
                          e.stopPropagation();
                          onBorrow(b.id);
                        }}
                      >
                        {t("library.borrow", { fallback: "Borrow" })}
                      </button>
                    )}
                    {canEdit && !lockEffective && (
                      <>
                        <button
                          className="btn btn-ghost"
                          style={{
                            padding: "2px 5px",
                            fontSize: FS.s9,
                            marginLeft: 3,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMod({ ...b, _touched: {} });
                          }}
                        >
                          <Pencil size={I.s} />
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{
                            padding: "2px 5px",
                            fontSize: FS.s9,
                            marginLeft: 2,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDelConfirm(b.id);
                          }}
                        >
                          <Trash2 size={I.s} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {totPages > 1 && <Pager cur={curPage} tot={totPages} setPage={setPage} />}

      {delConfirm !== null && (
        <ModalLayout
          title={t("library.delete_book", { fallback: "Delete book?" })}
          onClose={() => setDelConfirm(null)}
          closeOnOverlay
          maxWidth={360}
          footer={
            <div className="modal-footer">
              <button
                type="button"
                className="modal-cancel"
                onClick={() => setDelConfirm(null)}
              >
                {t("common.cancel", { fallback: "Cancel" })}
              </button>
              <button
                type="button"
                className="modal-delete"
                onClick={() => delBook(delConfirm)}
              >
                {t("common.delete", { fallback: "Delete" })}
              </button>
            </div>
          }
        >
              <p style={{ fontSize: FS.s11, color: "var(--ts)" }}>
                {t("common.cannot_be_undone", {
                  fallback: "This cannot be undone.",
                })}
              </p>
        </ModalLayout>
      )}

      {mod && (
        <ModalLayout
          title={
            mod.id
              ? t("library.edit_book", { fallback: "Edit Book" })
              : t("library.add_book", { fallback: "Add Book" })
          }
          onClose={() => setMod(null)}
          stopShellClicks
          maxWidth={520}
          rawBody
          footer={
            <div className="modal-footer">
              <button
                type="button"
                className="modal-cancel"
                onClick={() => setMod(null)}
              >
                {t("common.cancel", { fallback: "Cancel" })}
              </button>
              <button type="button" className="modal-save" onClick={saveBook}>
                ✓ {t("common.save", { fallback: "Save" })}
              </button>
            </div>
          }
        >
            <div className="modal-body">
              {/* ISBN row — lookup + scan live at the top so users can populate
              the rest of the form by entering or scanning a barcode. */}
              <div className="field">
                <label>{t("library.isbn", { fallback: "ISBN" })}</label>
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    className="field-input"
                    value={mod.isbn || ""}
                    onChange={(e) => setF("isbn", e.target.value)}
                    style={{ flex: 1 }}
                    placeholder="978…"
                  />
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{
                      padding: "4px 10px",
                      fontSize: FS.s10,
                      whiteSpace: "nowrap",
                    }}
                    onClick={runLookup}
                    disabled={lookupBusy || !mod.isbn}
                  >
                    {lookupBusy ? (
                      <RefreshCw
                        size={I.s}
                        className="spin"
                        style={{ marginRight: 3, verticalAlign: "middle" }}
                      />
                    ) : (
                      <BookMarked
                        size={I.s}
                        style={{ marginRight: 3, verticalAlign: "middle" }}
                      />
                    )}
                    {t("library.lookup", { fallback: "Lookup" })}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{
                      padding: "4px 10px",
                      fontSize: FS.s10,
                      whiteSpace: "nowrap",
                      opacity: scanSupported ? 1 : 0.5,
                    }}
                    onClick={() => scanSupported && setScanOpen(true)}
                    disabled={!scanSupported}
                    title={
                      scanSupported
                        ? t("library.scan_isbn", {
                            fallback: "Scan ISBN barcode",
                          })
                        : t("library.scan_unsupported", {
                            fallback:
                              "Barcode scan not supported in this browser",
                          })
                    }
                  >
                    <Camera
                      size={I.s}
                      style={{ marginRight: 3, verticalAlign: "middle" }}
                    />
                    {t("library.scan", { fallback: "Scan" })}
                  </button>
                </div>
              </div>
              <div className="field">
                <label>{t("library.author", { fallback: "Author" })} *</label>
                <input
                  className="field-input"
                  value={mod.author || ""}
                  onChange={(e) => setF("author", e.target.value)}
                  autoFocus
                />
              </div>
              <div className="field">
                <label>{t("common.title", { fallback: "Title" })} *</label>
                <input
                  className="field-input"
                  value={mod.title || ""}
                  onChange={(e) => setF("title", e.target.value)}
                />
              </div>
              <div className="field-row" style={{ gridTemplateColumns: "2fr 1fr" }}>
                <div className="field">
                  <label>
                    {t("library.publisher", { fallback: "Publ./Journ." })}
                  </label>
                  <input
                    className="field-input"
                    value={mod.publisher || ""}
                    onChange={(e) => setF("publisher", e.target.value)}
                  />
                </div>
                <div className="field">
                  <label>{t("library.year", { fallback: "Year" })}</label>
                  <input
                    className="field-input"
                    value={mod.year || ""}
                    onChange={(e) => setF("year", e.target.value)}
                  />
                </div>
              </div>
              <div className="field-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <div className="field">
                  <label>{t("library.section", { fallback: "Section" })}</label>
                  <Combobox
                    value={mod.section || ""}
                    onChange={(v) => setF("section", v)}
                    options={secOptions.map((o: any) => ({
                      value: o.code,
                      label: `${o.label} (${o.code})`,
                      sub: o.code,
                    }))}
                    placeholder={t("common.none", { fallback: "— none —" })}
                  />
                </div>
                <div className="field">
                  <label>{t("library.code", { fallback: "Code" })}</label>
                  <input
                    className="field-input"
                    value={mod.code || ""}
                    onChange={(e) => setF("code", e.target.value)}
                    placeholder={
                      mod.section ? `${mod.section} - C0000` : "C0000"
                    }
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginTop: 6,
                  flexWrap: "wrap",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: FS.s11,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!mod.paper}
                    onChange={(e) => setF("paper", e.target.checked)}
                    style={{ accentColor: paperAccent(mod) }}
                  />
                  <BookOpen size={12} />{" "}
                  {t("library.paper", { fallback: "Paper" })}
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: FS.s11,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!mod.pdf}
                    onChange={(e) => setF("pdf", e.target.checked)}
                    style={{ accentColor: greenAccent(mod.pdf) }}
                  />
                  <FileText size={12} /> {t("library.pdf", { fallback: "PDF" })}
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: FS.s11,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!mod.strada}
                    onChange={(e) => setF("strada", e.target.checked)}
                    style={{ accentColor: greenAccent(mod.strada) }}
                  />
                  <BookMarked size={12} />{" "}
                  {t("library.strada", { fallback: "Strada" })}
                </label>
              </div>
              {/* Phase 18.1 — 000BIB attachment block. Gated solely on
              lateral.library.edit; visible in BOTH Add and Edit modes
              (an Add-mode pick stages the file and the chained
              create+upload runs on Save). Bare lateral.library users
              never see this block — the Wikipedia-style add path can
              create rows but not attach files. */}
              {_canEditCatalog && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 10,
                    border: "1px solid var(--br)",
                    borderRadius: RAD.r8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <Paperclip size={12} style={{ color: "var(--tm)" }} />
                    <strong style={{ fontSize: FS.s11 }}>
                      {t("library.pdf_attachment", {
                        fallback: "PDF attachment (000BIB)",
                      })}
                    </strong>
                  </div>
                  {mod.id && mod.pdfFileName ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      {mod.pdfWebUrl ? (
                        <a
                          href={mod.pdfWebUrl}
                          target="_blank"
                          rel="noopener"
                          style={{
                            fontSize: FS.s11,
                            color: "var(--gnm)",
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <ExternalLink size={11} /> {mod.pdfFileName}
                        </a>
                      ) : (
                        <span style={{ fontSize: FS.s11, color: "var(--tp)" }}>
                          {mod.pdfFileName}
                        </span>
                      )}
                      {/* Phase 18.2 — peer styling to Detach so the action
                      reads unambiguously as a button (not a link or a
                      passive state indicator). Accent-blue treatment
                      vs Detach's red marks it as non-destructive. */}
                      <button
                        type="button"
                        className="btn"
                        style={{
                          padding: "3px 10px",
                          fontSize: FS.s10_5,
                          fontWeight: 600,
                          background: "rgba(74,124,255,.12)",
                          color: "var(--ac)",
                          border: "1px solid rgba(74,124,255,.35)",
                          borderRadius: RAD.r4,
                        }}
                        onClick={() => openCasePicker(mod.id)}
                        disabled={pdfBusy}
                      >
                        {t("library.copy_to_case", {
                          fallback: "Copy to case…",
                        })}
                      </button>
                      <button
                        type="button"
                        className="btn"
                        style={{
                          padding: "3px 10px",
                          fontSize: FS.s10_5,
                          fontWeight: 600,
                          background: "rgba(248,113,113,.1)",
                          color: "var(--rd)",
                          border: "1px solid rgba(248,113,113,.3)",
                          borderRadius: RAD.r4,
                        }}
                        onClick={onPdfDetach}
                        disabled={pdfBusy}
                      >
                        {t("library.detach", { fallback: "Detach" })}
                      </button>
                    </div>
                  ) : pdfStaged ? (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                          marginBottom: 6,
                        }}
                      >
                        <span style={{ fontSize: FS.s11, color: "var(--tp)" }}>
                          {pdfStaged.name}
                        </span>
                        <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                          {t("common.size_mb", { n: (pdfStaged.size / 1024 / 1024).toFixed(2), fallback: "{n} MB" })}
                        </span>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ padding: "2px 7px", fontSize: FS.s10 }}
                          onClick={() => {
                            setPdfStaged(null);
                            setPdfPlan(null);
                          }}
                          disabled={pdfBusy}
                        >
                          {t("common.clear", { fallback: "Clear" })}
                        </button>
                      </div>
                      {/* Plan-derived clash warning is Edit-only — Add mode
                      has no id to plan against, so the dedup runs on
                      the server at upload time and (if it suffixes) is
                      surfaced in the post-save toast. */}
                      {mod.id && pdfPlan && (
                        <p
                          style={{
                            fontSize: FS.s10_5,
                            color: "var(--tm)",
                            margin: "2px 0 6px",
                          }}
                        >
                          {t("library.will_upload_as", {
                            name: pdfPlan.filename,
                            fallback: "Will upload as",
                          })}
                          : <code>{pdfPlan.filename}</code>
                        </p>
                      )}
                      {mod.id && pdfPlan?.baseExists && (
                        <p
                          style={{
                            fontSize: FS.s10_5,
                            color: "var(--rd)",
                            margin: "2px 0 6px",
                            fontWeight: 500,
                          }}
                        >
                          ⚠{" "}
                          {t("library.name_clash_warning", {
                            name: pdfPlan.baseName,
                            fallback: `A file named "${pdfPlan.baseName}.pdf" already exists in 000BIB. The upload will use the next available suffix.`,
                          })}
                        </p>
                      )}
                      {mod.id ? (
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ padding: "4px 10px", fontSize: FS.s11 }}
                          onClick={onPdfUpload}
                          disabled={pdfBusy}
                        >
                          {pdfBusy ? (
                            <RefreshCw
                              size={11}
                              className="spin"
                              style={{
                                marginRight: 4,
                                verticalAlign: "middle",
                              }}
                            />
                          ) : (
                            <Upload
                              size={11}
                              style={{
                                marginRight: 4,
                                verticalAlign: "middle",
                              }}
                            />
                          )}
                          {t("library.upload_pdf", { fallback: "Upload PDF" })}
                        </button>
                      ) : (
                        <p
                          style={{
                            fontSize: FS.s10_5,
                            color: "var(--ac)",
                            margin: "2px 0 0",
                            fontStyle: "italic",
                          }}
                        >
                          {t("library.uploads_on_save", {
                            fallback:
                              "The PDF will upload when you click Save.",
                          })}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ padding: "4px 10px", fontSize: FS.s11 }}
                        onClick={() => pdfFileRef.current?.click()}
                        disabled={pdfBusy}
                      >
                        <Upload
                          size={11}
                          style={{ marginRight: 4, verticalAlign: "middle" }}
                        />
                        {t("library.pick_pdf", { fallback: "Pick PDF…" })}
                      </button>
                      <input
                        ref={pdfFileRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (f) onPdfPicked(f);
                        }}
                      />
                      <p
                        style={{
                          fontSize: FS.s10,
                          color: "var(--tm)",
                          margin: "4px 0 0",
                        }}
                      >
                        {t("library.pdf_help", {
                          fallback:
                            "Saves to /000BIB on SharePoint. Filename: YYYY-Title-Author-Journal.pdf",
                        })}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
        </ModalLayout>
      )}

      {/* Phase 18 — bib-side case picker. Opens from the modal's
        "Copy to case…" button. Lists both regular cases and collective
        cases, searchable. Picking a row dispatches to the matching
        endpoint and closes on success. */}
      {caseCopy &&
        (() => {
          const ql = caseCopyQ.trim().toLowerCase();
          const rows = [];
          // Phase 18.3 — filter the picker to cases the user can actually
          // write documents to. _perms.can() is a sync local-state lookup
          // (no network), so per-case checks are cheap even on large
          // case lists. The destination case's caseView.documents.edit
          // gate is also enforced server-side by the copy endpoint; this
          // is purely UX to spare a fail-then-toast cycle on cases the
          // user could see but not write to.
          for (const c of caseList || []) {
            if (!_perms.can("caseView.documents.edit", { caseId: c.id }))
              continue;
            if (
              !ql ||
              (c.ref || "").toLowerCase().includes(ql) ||
              (c.title || "").toLowerCase().includes(ql)
            ) {
              rows.push({
                id: c.id,
                kind: "case",
                ref: c.ref || "—",
                title: c.title || "",
              });
            }
            if (rows.length > 200) break;
          }
          for (const c of ccList || []) {
            if (!_perms.can("caseView.documents.edit", { ccId: c.id }))
              continue;
            if (
              !ql ||
              (c.ref || "").toLowerCase().includes(ql) ||
              (c.title || "").toLowerCase().includes(ql)
            ) {
              rows.push({
                id: c.id,
                kind: "cc",
                ref: c.ref || "—",
                title: c.title || "",
              });
            }
            if (rows.length > 400) break;
          }
          return (
            <ModalLayout
              title={t("library.copy_to_case_title", {
                fallback: "Copy to which case?",
              })}
              onClose={() => setCaseCopy(null)}
              closeOnOverlay
              closeDisabled={caseCopy.busy}
              stopShellClicks
              maxWidth={520}
              rawBody
              footer={null}
            >
                <div className="modal-body">
                  <div style={{ position: "relative", marginBottom: 8 }}>
                    <Search
                      size={11}
                      style={{
                        position: "absolute",
                        left: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "var(--tm)",
                      }}
                    />
                    <input
                      className="field-input"
                      autoFocus
                      value={caseCopyQ}
                      onChange={(e) => setCaseCopyQ(e.target.value)}
                      placeholder={t("library.case_search_placeholder", {
                        fallback: "Search by reference or title…",
                      })}
                      style={{
                        margin: 0,
                        padding: "5px 8px 5px 24px",
                        fontSize: FS.s11,
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      maxHeight: 320,
                      overflowY: "auto",
                      border: "1px solid var(--br)",
                      borderRadius: RAD.r6,
                    }}
                  >
                    {rows.length === 0 && (
                      <p
                        style={{
                          fontSize: FS.s11,
                          color: "var(--tm)",
                          padding: 10,
                          textAlign: "center",
                        }}
                      >
                        {t("library.no_cases_match", {
                          fallback: "No cases match.",
                        })}
                      </p>
                    )}
                    {rows.map((r) => (
                      <div
                        key={`${r.kind}-${r.id}`}
                        onClick={() => !caseCopy.busy && doCasePick(r)}
                        style={{
                          padding: "6px 10px",
                          cursor: caseCopy.busy ? "wait" : "pointer",
                          borderBottom: "1px solid rgba(255,255,255,.04)",
                          fontSize: FS.s11,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "var(--bh)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "none")
                        }
                      >
                        <div style={{ color: "var(--tp)", fontWeight: 500 }}>
                          <span
                            style={{
                              fontFamily: "'JetBrains Mono',monospace",
                              fontSize: FS.s10,
                              marginRight: 6,
                              color:
                                r.kind === "cc" ? "var(--am)" : "var(--ac)",
                            }}
                          >
                            {r.kind === "cc"
                              ? t("library.badge_cc", { fallback: "CC" })
                              : t("library.badge_case", { fallback: "CASE" })}
                          </span>
                          {r.ref}
                        </div>
                        <div style={{ color: "var(--tm)", fontSize: FS.s10 }}>
                          {r.title}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
            </ModalLayout>
          );
        })()}

      {scanOpen && (
        <BarcodeScanner
          onClose={() => setScanOpen(false)}
          onDetect={onScanned}
          toast={toast}
          t={t}
        />
      )}

      {/* 216 addendum 5 §9 — the preview window, before anything is written */}
      {importAsk && (
        <LibraryImportModal
          plan={importAsk.plan}
          mode={importAsk.mode}
          setMode={(m) => {
            setImportAsk((p) => (p ? { ...p, mode: m } : p));
          }}
          busy={importAsk.busy}
          onClose={() => {
            setImportAsk(null);
          }}
          onApply={runImport}
          t={t as Translate}
        />
      )}
      {importReport && (
        <ModalLayout
          title={t("library.import_report", { fallback: "Import report" })}
          onClose={() => setImportReport(null)}
          closeOnOverlay
          maxWidth={560}
          footer={
            <div className="modal-footer">
              <button
                type="button"
                className="modal-save"
                onClick={() => setImportReport(null)}
              >
                {t("common.close", { fallback: "Close" })}
              </button>
            </div>
          }
        >
              <p style={{ fontSize: FS.s12 }}>
                <Check
                  size={12}
                  style={{
                    marginRight: 4,
                    verticalAlign: "middle",
                    color: "var(--gn)",
                  }}
                />
                {t("library.imported_n_books", {
                  count: importReport.imported,
                  fallback: `Imported ${importReport.imported} book${importReport.imported !== 1 ? "s" : ""}`,
                })}
                {importReport.warnings.length > 0 && (
                  <span style={{ marginLeft: 8, color: "var(--am)" }}>
                    {(() => {
                      const n = Number(importReport.warnings.length); // read once: the number, and the word's form (209 §7)
                      return ["· ", n, " ", (t as Translate)("library.warnings", { count: n, fallback: "warnings" })];
                    })()}
                  </span>
                )}
              </p>
              {importReport.warnings.length > 0 && (
                <div
                  style={{
                    maxHeight: 320,
                    overflowY: "auto",
                    border: "1px solid var(--br)",
                    borderRadius: RAD.r6,
                    marginTop: 8,
                  }}
                >
                  <Table style={{ width: "100%", fontSize: FS.s11 }}>
                    <thead>
                      <tr>
                        <th
                          style={{
                            width: 60,
                            textAlign: "left",
                            padding: "4px 8px",
                          }}
                        >
                          {t("library.row", { fallback: "Row" })}
                        </th>
                        <th style={{ textAlign: "left", padding: "4px 8px" }}>
                          {t("library.warning", { fallback: "Warning" })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {importReport.warnings.map((w: any, i: any) => (
                        <tr key={i}>
                          <td
                            style={{ padding: "3px 8px", color: "var(--tm)" }}
                          >
                            {w.row}
                          </td>
                          <td style={{ padding: "3px 8px" }}>{w.msg}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
        </ModalLayout>
      )}
    </>
  );
};

// Barcode scanner overlay. Lives as a sibling component because it owns
// the video stream lifecycle: mounting opens the camera, unmounting tears
// it down. Embedding the stream logic in LibraryView would have leaked
// the MediaStream across modal opens.
function BarcodeScanner({ onClose, onDetect, toast, t }: any) {
  const videoRef = useRef<any>(null);
  const [err, setErr] = useState<any>(null);
  useEffect(() => {
    let stream: any;
    let stopped = false;
    let raf: any;
    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (stopped) {
          stream.getTracks().forEach((tr: any) => tr.stop());
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        // EAN-13 covers ISBN-13; UPC-A is the US 12-digit equivalent and
        // is convertible. Both formats are passed in case the scanner
        // hands either one back.
        // `!` because reaching here already required the support probe at
        // :1036 (`"BarcodeDetector" in window`), which is too far away for the
        // compiler to carry the narrowing. Erased, like everything else here.
        const detector = new window.BarcodeDetector!({
          formats: ["ean_13", "upc_a"],
        });
        const tick = async () => {
          if (stopped || !videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes && codes.length > 0 && codes[0].rawValue) {
              onDetect(codes[0].rawValue);
              return;
            }
          } catch {
            /* keep scanning */
          }
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch (e: any) {
        setErr(e.message || String(e));
      }
    };
    start();
    return () => {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      if (stream) stream.getTracks().forEach((tr: any) => tr.stop());
    };
  }, [onDetect]);
  return (
    <ModalLayout
      title={t("library.scan_isbn", { fallback: "Scan ISBN barcode" })}
      onClose={onClose}
      closeOnOverlay
      stopShellClicks
      maxWidth={480}
      rawBody
      footer={
        <div className="modal-footer">
          <button type="button" className="modal-cancel" onClick={onClose}>
            {t("common.cancel", { fallback: "Cancel" })}
          </button>
        </div>
      }
    >
        <div className="modal-body">
          {err ? (
            <p style={{ fontSize: FS.s12, color: "var(--rd)" }}>
              {t("library.scan_error", {
                fallback: "⚠ Could not access camera",
              })}
              : {err}
            </p>
          ) : (
            <video
              ref={videoRef}
              playsInline
              muted
              style={{ width: "100%", borderRadius: RAD.r6, background: CT.c_000 }}
            />
          )}
          <p style={{ fontSize: FS.s10_5, color: "var(--tm)", marginTop: 8 }}>
            {t("library.scan_hint", {
              fallback: "Hold the EAN-13 barcode steady in the camera view.",
            })}
          </p>
        </div>
    </ModalLayout>
  );
}

export default LibraryView;
